import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import moment from "moment";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { AccountService, FinanceService, ProjectService } from "../../services";
import { expenseValidationRules } from "../../pages/expense/validation";
import { get_date } from "../generalComponets";
import { useHasPermission } from "../../utils";
import {
  ENABLE_EXPENSE_BACKDATE_ENTRY,
  CREATE_FINANCE_CATEGORY,
  VIEW_ACCOUNT,
  SELECT_OFFICE_ACCOUNT_IN_ENTRIES,
} from "../../constants";
import { AddEditCategory } from "../library";
import { markTableReset, RESTORE_TABLE_STATE } from "../../utils/tableStateStorage";

export const AddEditExpenseForm = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [expenseDetails, setExpenseDetails] = useState({
    amount: "",
    date: get_date(Date.now(), "YYYY-MM-DD"),
    categoryId: null,
    status: "COMPLETED",
    description: "",
    projectId: null,
    projectStageId: undefined,
    isPaid: true,
    paidAmount: null,
    fromAccountId: null,
  });
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectStage, setSelectedProjectStage] = useState(null);
  const [stageIds, setStageIds] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const { user: currentUser } = useSelector((state) => state.user);
  const { ownerUserId } = currentUser.company;

  const [searchParams] = useSearchParams();
  const stayInPage = searchParams.get("stayInPage");
  const projectId = searchParams.get("projectId");
  const accountType = searchParams.get("accountType");

  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const enableBackDate = useHasPermission(ENABLE_EXPENSE_BACKDATE_ENTRY);
  const hasSelectOfficeAccount = useHasPermission(SELECT_OFFICE_ACCOUNT_IN_ENTRIES);
  const navigate = useNavigate();
  const { expenseId } = useParams();

  const hasCreateCategory = useHasPermission(CREATE_FINANCE_CATEGORY);

  useEffect(() => {
    fetchAccounts();

    if (expenseId) {
      fetchData(expenseId);
    } else if (projectId) {
      fetchProject(projectId);
    } else if (accountType === "office") {
      setSelectedProject({ id: "office", name: "Company/Office" });
      setExpenseDetails((prev) => ({ ...prev, projectId: "office" }));
    }
  }, []);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
      sortBy: "name",
      sortOrder: "asc",
    }),
    [stageIds],
  );

  const fetchData = async (id) => {
    try {
      const { data: expense } = await FinanceService.getExpense(id);
      // fetch project stages
      if (expense?.projectId && expense?.projectId !== "office") fetchProjectStages(expense?.projectId);

      // fetch stages
      if (expense?.projectStageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(expense?.projectStage?.stageId);
          setSelectedProjectStage(stage);
        } catch (error) {
          toast.error("Failed to fetch stage details");
          console.error("🚀 ~ fetchData ~ error:", error);
        }
      }
      setExpenseDetails({
        amount: Number(expense.amount),
        date: new Date(expense.date).toISOString().split("T")[0],
        categoryId: expense.categoryId,
        status: expense.status,
        description: expense.description,
        projectId: expense.projectId === null ? "office" : expense.projectId,
        fromAccountId: expense.meta?.fromAccountId,
      });
      setSelectedCategory(expense.category);
      setSelectedProject(expense.project || (expense.projectId === null ? { id: "office", name: "Company/Office" } : null));
    } catch (error) {
      console.error("Error fetching expense:", error);
      setErrors({ fetch: "Failed to fetch expense" });
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data: accounts } = await AccountService.getAdminAndUserAccounts({
        hasViewAccount,
        user: currentUser,
        companyAdminId: ownerUserId,
      });
      const userAccount = currentUser.accounts[0];
      setSelectedAccount(userAccount);
      setExpenseDetails((prev) => ({
        ...prev,
        fromAccountId: userAccount?.id,
      }));
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to fetch accounts");
    }
  };

  const fetchProject = async (projectId) => {
    try {
      const { data: project } = await ProjectService.getProjectById(projectId);
      handleProjectChange(project);
    } catch (error) {
      console.error("🚀 ~ fetchProject ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setExpenseDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (selected) => {
    setSelectedCategory(selected);
    setExpenseDetails((prev) => ({ ...prev, categoryId: selected?.id || null }));
  };
  const toggleCategoryModal = () => setOpenCategoryModal((prev) => !prev);
  const handleCategoryAddSuccess = (newCategory) => handleCategoryChange(newCategory);

  const handleProjectChange = (selected) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setExpenseDetails((prev) => ({ ...prev, projectId: selected?.id || null, projectStageId: null }));
    if (selected?.id && selected?.id !== "office") fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected) => {
    const projectStage = projectStages.find((item) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setExpenseDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleAccountChange = (selected) => {
    setSelectedAccount(selected);
    setExpenseDetails((prev) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleClearForm = () => {
    setErrors({});
    setExpenseDetails({
      amount: undefined,
      date: get_date(Date.now(), "YYYY-MM-DD"),
      categoryId: undefined,
      status: "COMPLETED",
      description: "",
      projectId: undefined,
      projectStageId: undefined,
      isPaid: true,
      paidAmount: undefined,
      fromAccountId: selectedAccount?.id || undefined,
    });
    setSelectedCategory(null);
    setSelectedProject(null);
    setSelectedProjectStage(null);
  };

  const handleSaveExpense = async () => {
    setErrors({});
    const errors = expenseValidationRules(expenseDetails);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setLoading(true);
    // convert amount to number and manipulate date to convert to ISOString
    const currentTime = moment().format("HH:mm:ss"); // Get current time
    const fullDateTime = moment(`${expenseDetails.date} ${currentTime}`).toISOString();
    const payload = {
      ...expenseDetails,
      amount: Number(expenseDetails.amount),
      date: fullDateTime,
      projectId: expenseDetails.projectId === "office" ? null : expenseDetails.projectId,
    };
    try {
      if (expenseId) {
        // delete fromAccountId from payload as it should not be editable
        delete payload.fromAccountId;
        await FinanceService.updateExpense(expenseId, payload);
      } else {
        payload.paidAmount = payload.amount;
        await FinanceService.createExpense(payload);
      }
      toast.success(`Expense ${expenseId ? "updated" : "added"} successfully`);

      handleClearForm();
      // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
      if (projectId) navigate(`/projects/${projectId}?tab=expense`, expenseId ? { state: RESTORE_TABLE_STATE } : undefined);
      else if (accountType === "office" || !stayInPage) {
        if (!expenseId) markTableReset();
        navigate(-1);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || `Failed to ${expenseId ? "update" : "add"} expense `;
      toast.error(errorMessage);
      console.error(`🚀 ~ handleEditExpense ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <InputField
          required
          label="Amount"
          name="amount"
          type="number"
          value={expenseDetails.amount || ""}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={expenseDetails.date || ""}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <SelectField
          label="Category"
          value={selectedCategory}
          getData={FinanceService.getCategories}
          getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
          required
          error={!!errors.categoryId}
          helperText={errors.categoryId}
          onChange={handleCategoryChange}
          addButtonText="Add Category"
          showAddButton={hasCreateCategory}
          onAddClick={toggleCategoryModal}
          style={{ width: "100%" }}
        />
      </Box>
      <Box display="flex" gap={2}>
        <SelectField
          label="Project"
          required
          value={selectedProject}
          defaultOption={hasSelectOfficeAccount ? { id: "office", name: "Company/Office" } : undefined} // TODO: replace this with a proper option for company/office project when available in the API
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["INPROGRESS"], sortBy: "name", sortOrder: "asc" }}
          disabled={!!projectId || accountType === "office"}
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Stage"
          value={selectedProjectStage}
          getData={ProjectService.getStages}
          getDataStaticFilters={stageFilter}
          disabled={stageIds.length === 0 || expenseDetails.projectId === "office"}
          error={!!errors.projectStageId}
          helperText={errors.projectStageId}
          onChange={handleProjectStageChange}
          style={{ width: "100%" }}
        />
        <InputField
          label="Description"
          name="description"
          value={expenseDetails.description || ""}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          autoComplete="on"
        />
      </Box>
      {!expenseId && (
        <Box display="flex" gap={2}>
          <SelectField
            label="From Account"
            value={selectedAccount}
            getData={AccountService.getAdminAndUserAccounts}
            getDataStaticFilters={{ hasViewAccount, user: currentUser, companyAdminId: ownerUserId }}
            required
            error={!!errors.fromAccountId}
            helperText={errors.fromAccountId}
            onChange={handleAccountChange}
            style={{ width: "100%" }}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <ActionButton buttonText="Cancel" onButtonClick={handleCancelClick} variant="outlined" />
        <ActionButton buttonText="Save" onButtonClick={handleSaveExpense} loading={loading} />
      </Box>

      {/* add category modal */}
      <AddEditCategory open={openCategoryModal} onClose={toggleCategoryModal} onSuccess={handleCategoryAddSuccess} />
    </CardLayout>
  );
};
